import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { Link, Navigate, useLocation, useParams } from "react-router-dom";
import { formatMoney, isActiveContractor, type Me, type Order } from "@neus/shared";
import { ApiError } from "../api/client";
import { useContractorOrders, useMe, useMyOrders } from "../api/hooks";
import { CartIcon, ClipboardIcon } from "../components/Icons";
import { IconCard, LoadError, PageTitle } from "../components/ui/IconCard";
import { JobOrdersTable } from "./Contractors";
import { PickupActions, PickupQr, placedOn } from "./PickupOrder";
import styles from "./Orders.module.css";

const message = (err: unknown) => (err instanceof Error ? err.message : "Something went wrong. Please try again.");

const pickupPage = (id: string) => `/account/orders/${encodeURIComponent(id)}`;

function StoreOrder({ order }: { order: Order }) {
  const unpaid = order.status === "unpaid";
  return (
    <li className={styles.order}>
      <div className={styles.head}>
        <div>
          <strong>{placedOn(order.createdAt)}</strong>
          <div className="muted">
            {unpaid ? (
              <>
                <Link to={pickupPage(order.id)}>Order {order.id}</Link> · Pick up in store, pay there
              </>
            ) : (
              <>Order {order.id}</>
            )}
          </div>
        </div>
        <strong>{formatMoney(order.total, order.currency)}</strong>
      </div>
      <OrderLines order={order} />
    </li>
  );
}

/** An order's lines and totals. */
function OrderLines({ order }: { order: Order }) {
  const money = (cents: number) => formatMoney(cents, order.currency);
  const unpaid = order.status === "unpaid";
  return (
    <>
      <ul className={styles.lines}>
        {order.lines.map((l) => (
          <li key={l.sku}>
            <span>
              <Link to={`/products/${encodeURIComponent(l.sku)}`}>{l.name}</Link>
              <span className="muted"> × {l.qty}</span>
            </span>
            <span className={styles.amount}>{money(l.lineTotal)}</span>
          </li>
        ))}
      </ul>
      <div className={`muted ${styles.totals}`}>
        {unpaid
          ? "Not paid yet: you pay at the register when you pick it up, and sales tax is added there."
          : `Shipping ${order.shipping === 0 ? "free" : money(order.shipping)} · Sales tax ${money(order.tax)}`}
      </div>
    </>
  );
}

function StoreOrders() {
  const { data: orders, isLoading, error } = useMyOrders();
  const qc = useQueryClient();
  // The session ended since the account loaded (expired, or signed out everywhere): sign in again.
  const signedOut = error instanceof ApiError && error.status === 401;
  useEffect(() => {
    if (signedOut) qc.setQueryData(["me"], null);
  }, [signedOut, qc]);
  return (
    <IconCard icon={<CartIcon />} title="Store orders">
      {isLoading ? (
        <p className="muted">Loading…</p>
      ) : error ? (
        <div className="notice notice--warn">{message(error)}</div>
      ) : !orders?.length ? (
        <p className="muted">
          No orders yet. Orders you place while signed in show up here. <Link to="/products">Start shopping</Link>.
        </p>
      ) : (
        <ul className={styles.orders}>
          {orders.map((o) => (
            <StoreOrder key={o.id} order={o} />
          ))}
        </ul>
      )}
    </IconCard>
  );
}

function JobOrders() {
  const { data: orders, isLoading, error } = useContractorOrders();
  // The contractor program is off on this server.
  if (error instanceof ApiError && error.status === 404) return null;
  return (
    <IconCard icon={<ClipboardIcon />} title="Job orders">
      {isLoading ? (
        <p className="muted">Loading…</p>
      ) : error ? (
        <div className="notice notice--warn">{message(error)}</div>
      ) : !orders?.length ? (
        <p className="muted">
          No job orders yet. <Link to="/contractor/orders/new">Start a job order</Link>.
        </p>
      ) : (
        <JobOrdersTable orders={orders} />
      )}
    </IconCard>
  );
}

/** Just sent from the cart: where it's going, and its Print and Send buttons. */
function PickupSent({ id }: { id: string }) {
  const order = useMyOrders().data?.find((o) => o.id === id);
  return (
    <div className={`notice ${styles.sent}`} role="status">
      <p>
        Order {id} is on its way to the store. We&rsquo;ll gather it for you; pay at the register when you pick it up.
        Print it, or send it to your phone, to show its QR code there.
      </p>
      {order && <PickupActions order={order} />}
    </div>
  );
}

function OrderHistory({ me }: { me: Me }) {
  // Just sent from the cart for pickup (Cart.tsx).
  const pickupOrder = (useLocation().state as { pickupOrder?: string } | null)?.pickupOrder;
  return (
    <>
      <PageTitle subtitle="Orders you’ve placed while signed in.">Your orders</PageTitle>
      {pickupOrder && <PickupSent id={pickupOrder} />}
      <StoreOrders />
      {isActiveContractor(me) && <JobOrders />}
    </>
  );
}

export function OrdersPage() {
  const { data: me, isLoading, isError, refetch } = useMe();
  let content;
  if (isLoading) content = <p className="muted">Loading…</p>;
  // An outage isn't "signed out" (api.me returns null for that), so don't send them to sign in.
  else if (isError) {
    content = <LoadError onRetry={() => void refetch()}>We couldn&rsquo;t load your account just now.</LoadError>;
  } else if (!me) return <Navigate to="/account/sign-in?next=%2Faccount%2Forders" replace />;
  else content = <OrderHistory me={me} />;
  return (
    <div className="container section">
      <div className={styles.page}>
        {isLoading || isError ? <PageTitle>Your orders</PageTitle> : null}
        {content}
      </div>
    </div>
  );
}

/** One pay-in-store pickup order (/account/orders/{number}): its QR code for the register, Print and Send. */
function PickupOrder({ id }: { id: string }) {
  const { data: orders, isLoading, error } = useMyOrders();
  const order = orders?.find((o) => o.id === id && o.status === "unpaid");
  if (isLoading) return <p className="muted">Loading…</p>;
  if (error) return <div className="notice notice--warn">{message(error)}</div>;
  if (!order) {
    return (
      <>
        <PageTitle>Order not found</PageTitle>
        <p className="muted">
          It isn&rsquo;t one of your pickup orders. <Link to="/account/orders">See your orders</Link>.
        </p>
      </>
    );
  }
  return (
    <>
      <PageTitle subtitle={`Placed ${placedOn(order.createdAt)} · Pick up in store, pay there`}>
        Order {order.id}
      </PageTitle>
      <div className={styles.pickup}>
        <PickupQr order={order} />
        <div>
          <p>
            Show this QR code at the register, on your phone or printed, and we&rsquo;ll bring out your order. You pay
            there, and sales tax is added then.
          </p>
          <PickupActions order={order} />
        </div>
      </div>
      <IconCard icon={<CartIcon />} title="Items">
        <OrderLines order={order} />
      </IconCard>
      <Link to="/account/orders">All your orders</Link>
    </>
  );
}

export function PickupOrderPage() {
  const id = useParams().id ?? "";
  const { data: me, isLoading, isError, refetch } = useMe();
  let content;
  if (isLoading) content = <p className="muted">Loading…</p>;
  // An outage isn't "signed out" (api.me returns null for that), so don't send them to sign in.
  else if (isError) {
    content = <LoadError onRetry={() => void refetch()}>We couldn&rsquo;t load your account just now.</LoadError>;
  } else if (!me) return <Navigate to={`/account/sign-in?next=${encodeURIComponent(pickupPage(id))}`} replace />;
  else content = <PickupOrder id={id} />;
  return (
    <div className="container section">
      <div className={styles.page}>{content}</div>
    </div>
  );
}
